import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, LogIn, Plus, Pencil, Trash2, FileText, Download, Upload, X, Search, TrendingUp, BarChart3, IndianRupee, RefreshCw, Sun, Moon, ChevronLeft, ChevronRight, Receipt, MessageCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie } from "recharts";
import { api, googleLogin, logout } from "@/lib/api";
import {
  STAGES, SOURCES, PRIORITIES, CLOSED, STAGE_WEIGHTS, STAGE_PALETTE, PRIORITY_CHART_COLORS,
  EMPTY_DEAL, inr, num, fmtNum, PRIORITY_COLORS, BrandLockup, inputCls, ChartBox, KpiCard,
  StageTip, PieTip, waDealLink,
} from "@/components/dealbook/shared";
import { DealDrawer } from "@/components/dealbook/DealDrawer";
import { InvoiceDrawer } from "@/components/dealbook/InvoiceDrawer";
import { InvoiceRegister } from "@/components/dealbook/InvoiceRegister";

const HERO_IMG = "https://images.pexels.com/photos/8135492/pexels-photo-8135492.jpeg";

export default function DealBook() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [state, setState] = useState(location.state?.user ? "ready" : "checking");
  const [deals, setDeals] = useState([]);
  const [theme, setTheme] = useState("light");
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dupDialog, setDupDialog] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ q: "", stage: "", source: "", priority: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [invoiceDeal, setInvoiceDeal] = useState(null);
  const [invoiceOpts, setInvoiceOpts] = useState({ gstin: "", place_of_supply: "Maharashtra", transaction_type: "Land Advisory Services", apply_tds: true, tds_rate: 2 });
  const [emailSending, setEmailSending] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [firmSettings, setFirmSettings] = useState({ gstin: "", place_of_supply: "", payment_terms: "" });
  const [paymentTerms, setPaymentTerms] = useState("");
  const [showInvoices, setShowInvoices] = useState(false);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (!res.data.is_admin) setState("denied");
        else { setUser(res.data); setState("ready"); }
      } catch { setState("denied"); }
    })();
  }, [location.state]);

  useEffect(() => {
    if (state === "denied") {
      api.get("/public/pipeline-summary").then((r) => setSummary(r.data)).catch(() => {});
    }
  }, [state]);

  const loadData = () => {
    api.get("/deals").then((r) => setDeals(r.data)).catch(() => toast.error("Could not load pipeline data"));
    api.get("/analytics/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/settings/firm").then((r) => {
      const s = r.data || {};
      setFirmSettings({ gstin: s.gstin || "", place_of_supply: s.place_of_supply || "", payment_terms: s.payment_terms || "" });
      setPaymentTerms(s.payment_terms || "");
      setInvoiceOpts((o) => ({
        ...o,
        gstin: o.gstin || s.gstin || "",
        place_of_supply: s.place_of_supply || o.place_of_supply,
      }));
    }).catch(() => {});
    setRefreshedAt(new Date());
  };

  useEffect(() => {
    if (state === "ready" && user?.is_admin) loadData();
  }, [state, user]);

  const filtered = useMemo(() => deals.filter((d) => {
    const q = filters.q.toLowerCase();
    const hit = !q || [d.client_name, d.address, d.source, d.landowner_name, d.plot_no, d.landmark]
      .filter(Boolean).some((x) => x.toLowerCase().includes(q));
    return hit && (!filters.stage || d.stage === filters.stage)
      && (!filters.source || d.source === filters.source)
      && (!filters.priority || d.priority === filters.priority);
  }), [deals, filters]);

  const open = deals.filter((d) => !CLOSED.includes(d.stage));
  const totalPipeline = open.reduce((s, d) => s + num(d.total_ask), 0);
  const feePotential = open.reduce((s, d) => s + num(d.fee_potential), 0);
  const avgDeal = open.length ? totalPipeline / open.length : 0;
  const weighted = open.reduce((s, d) => s + num(d.total_ask) * (num(d.probability) ? num(d.probability) / 100 : (STAGE_WEIGHTS[d.stage] ?? 0)), 0);
  const byStage = STAGES.map((s, i) => ({
    stage: s, value: deals.filter((d) => d.stage === s).reduce((a, d) => a + num(d.total_ask), 0), color: STAGE_PALETTE[i],
  })).filter((s) => s.value > 0);
  const priorityData = PRIORITIES.map((p) => ({
    name: p,
    value: deals.filter((d) => d.priority === p).reduce((a, d) => a + num(d.total_ask), 0),
    count: deals.filter((d) => d.priority === p).length,
  }));
  const priorityTotal = priorityData.reduce((s, p) => s + p.value, 0);
  const sourceMap = {};
  deals.forEach((d) => {
    const k = d.source || "Unknown";
    sourceMap[k] = sourceMap[k] || { value: 0, count: 0 };
    sourceMap[k].value += num(d.total_ask);
    sourceMap[k].count += 1;
  });
  const sourceData = Object.entries(sourceMap)
    .map(([name, v], i) => ({ name, value: v.value, count: v.count, color: STAGE_PALETTE[i % STAGE_PALETTE.length] }))
    .sort((a, b) => b.value - a.value);
  const sourceTotal = sourceData.reduce((s, x) => s + x.value, 0);

  const auto = (d) => {
    const resV = num(d.residential_sqft) * num(d.rate_per_sqft_residential);
    const comV = num(d.commercial_sqft) * num(d.rate_per_sqft_commercial);
    const total = num(d.residential_sqft) + num(d.commercial_sqft);
    const blended = total ? (resV + comV) / total : 0;
    const ask = num(d.total_ask) || (resV + comV);
    return {
      total, sqm: total / 10.764, blended,
      market: total * num(d.permissible_fsi) * blended,
      ask, fee: ask * num(d.expected_fee_pct) / 100,
    };
  };

  const saveDeal = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.deal_id) {
        const orig = deals.find((d) => d.deal_id === editing.deal_id) || {};
        const norm = (v) => (v === null || v === undefined ? "" : String(v));
        const patch = {};
        Object.keys(EMPTY_DEAL).forEach((k) => {
          if (norm(editing[k]) !== norm(orig[k])) patch[k] = editing[k];
        });
        if (!Object.keys(patch).length) {
          toast.info("No changes to save");
          setEditing(null);
          return;
        }
        const res = await api.patch(`/deals/${editing.deal_id}`, patch);
        setDeals((ds) => ds.map((d) => (d.deal_id === editing.deal_id ? res.data : d)));
        toast.success("Deal updated");
      } else {
        const res = await api.post("/deals", editing);
        setDeals((ds) => [res.data, ...ds]);
        toast.success("Deal created");
      }
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save deal");
    } finally { setSaving(false); }
  };

  const removeDeal = async (deal) => {
    if (!window.confirm(`Delete deal for ${deal.client_name}?`)) return;
    try {
      await api.delete(`/deals/${deal.deal_id}`);
      setDeals((ds) => ds.filter((d) => d.deal_id !== deal.deal_id));
      toast.success("Deal deleted");
    } catch { toast.error("Could not delete deal"); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/deals/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "keyscape-deal-book.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported to Excel");
    } catch { toast.error("Export failed"); }
  };

  const uploadExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const check = await api.post("/deals/upload?mode=check", fd);
      if (check.data.duplicates?.length) {
        setDupDialog({ file, ...check.data });
        toast.info(`${check.data.duplicates.length} duplicate record(s) found — choose how to proceed.`);
      } else {
        const fd2 = new FormData();
        fd2.append("file", file);
        const res = await api.post("/deals/upload?mode=skip", fd2);
        toast.success(`Imported ${res.data.imported} deals from Excel.`);
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const resolveDupes = async (mode) => {
    if (!dupDialog) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", dupDialog.file);
      const res = await api.post(`/deals/upload?mode=${mode}`, fd);
      const parts = [`${res.data.imported} new`];
      if (res.data.overwritten) parts.push(`${res.data.overwritten} overwritten`);
      if (res.data.created) parts.push(`${res.data.created} created as new`);
      if (res.data.skipped) parts.push(`${res.data.skipped} skipped`);
      toast.success(`Upload complete — ${parts.join(", ")}.`);
      setDupDialog(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const downloadPdf = async () => {
    if (!invoiceDeal) return;
    try {
      const params = new URLSearchParams({
        gstin: invoiceOpts.gstin,
        place_of_supply: invoiceOpts.place_of_supply,
        transaction_type: invoiceOpts.transaction_type,
        apply_tds: invoiceOpts.apply_tds,
        tds_rate: invoiceOpts.tds_rate,
      });
      const res = await api.get(`/deals/${invoiceDeal.deal_id}/invoice.pdf?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoiceDeal.deal_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
    } catch { toast.error("Download failed"); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };
  const set = (k) => (e) => setEditing((d) => ({ ...d, [k]: e.target.value }));

  const moveStage = async (d, dir) => {
    const idx = STAGES.indexOf(d.stage);
    const next = STAGES[idx + dir];
    if (!next) return;
    try {
      const res = await api.patch(`/deals/${d.deal_id}`, { ...EMPTY_DEAL, ...d, stage: next });
      setDeals((ds) => ds.map((x) => (x.deal_id === d.deal_id ? res.data : x)));
      toast.success(`${d.client_name} moved to ${next}`);
    } catch { toast.error("Could not update stage"); }
  };

  const emailInvoice = async (deal) => {
    const target = deal || invoiceDeal;
    if (!target) return;
    setEmailSending(true);
    try {
      const res = await api.post(`/deals/${target.deal_id}/invoice/email`);
      toast.success(`Invoice ${res.data.invoice_no} emailed to ${res.data.to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email failed");
    } finally { setEmailSending(false); }
  };

  const loadInvoices = async () => {
    try {
      const res = await api.get("/invoices");
      setInvoices(res.data);
    } catch { toast.error("Could not load invoices"); }
  };

  const setPaymentStatus = async (inv, status) => {
    try {
      if (inv.kind === "deal") {
        const d = deals.find((x) => x.deal_id === inv.id);
        if (d) {
          const res = await api.patch(`/deals/${inv.id}`, { ...EMPTY_DEAL, ...d, payment_status: status });
          setDeals((ds) => ds.map((x) => (x.deal_id === inv.id ? res.data : x)));
        }
      } else {
        await api.patch(`/admin/leads/${inv.id}`, { payment_status: status });
      }
      setInvoices((list) => list.map((x) => (x.id === inv.id && x.kind === inv.kind ? { ...x, payment_status: status } : x)));
      toast.success(`${inv.invoice_no} marked ${status}`);
    } catch { toast.error("Could not update payment status"); }
  };

  const sendReminder = async (inv) => {
    setReminderSending(true);
    try {
      const res = await api.post(`/deals/${inv.id}/invoice/reminder`);
      toast.success(`Reminder for ${res.data.invoice_no} emailed to ${res.data.to}`);
      setInvoices((list) => list.map((x) => (x.id === inv.id && x.kind === inv.kind
        ? { ...x, reminder_log: [...(x.reminder_log || []), { at: new Date().toISOString(), to: res.data.to }] } : x)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reminder failed");
    } finally { setReminderSending(false); }
  };

  const setAmountReceived = async (inv, value) => {
    const amt = Math.max(0, Number(value) || 0);
    try {
      if (inv.kind === "deal") {
        const d = deals.find((x) => x.deal_id === inv.id);
        if (d) {
          const res = await api.patch(`/deals/${inv.id}`, { ...EMPTY_DEAL, ...d, amount_received: amt });
          setDeals((ds) => ds.map((x) => (x.deal_id === inv.id ? res.data : x)));
        }
      } else {
        await api.patch(`/admin/leads/${inv.id}`, { amount_received: amt });
      }
      setInvoices((list) => list.map((x) => (x.id === inv.id && x.kind === inv.kind ? { ...x, amount_received: amt } : x)));
      toast.success(`Recorded ${inr(amt)} received on ${inv.invoice_no}`);
    } catch { toast.error("Could not save amount"); }
  };

  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const res = await api.post("/invoices/digest");
      if (res.data.status === "sent") toast.success(`Digest emailed — ${res.data.count} overdue invoice(s)`);
      else toast.info(`Digest not sent: ${res.data.reason}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Digest failed");
    } finally { setDigestSending(false); }
  };

  const saveFirmSettings = async () => {
    try {
      const res = await api.put("/settings/firm", {
        gstin: invoiceOpts.gstin,
        place_of_supply: invoiceOpts.place_of_supply,
        payment_terms: paymentTerms,
      });
      setFirmSettings(res.data);
      setInvoiceOpts((o) => ({ ...o, gstin: res.data.gstin, place_of_supply: res.data.place_of_supply || o.place_of_supply }));
      setPaymentTerms(res.data.payment_terms || "");
      toast.success("Firm defaults saved — every invoice will print them");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save firm defaults");
    }
  };

  const exportInvoices = async () => {
    try {
      const res = await api.get("/invoices/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "keyscape-invoice-register.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Invoice register exported");
    } catch { toast.error("Export failed"); }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const url = inv.kind === "deal" ? `/deals/${inv.id}/invoice.pdf` : `/admin/leads/${inv.id}/invoice.pdf?fee=${inv.fee}`;
      const res = await api.get(url, { responseType: "blob" });
      const obj = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = obj;
      a.download = `invoice-${inv.invoice_no}.pdf`;
      a.click();
      URL.revokeObjectURL(obj);
      toast.success("Invoice PDF downloaded");
    } catch { toast.error("Download failed"); }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center font-plex" data-testid="dealbook-loading">
        <p className="font-plexmono text-[11px] uppercase tracking-[0.3em] text-neutral-500 animate-pulse">Authenticating…</p>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div className="min-h-screen bg-[#0A0A0B] font-plex relative overflow-hidden" data-testid="dealbook-denied">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 min-h-screen flex flex-col px-8 md:px-14 py-10">
          <BrandLockup />
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-[1400px] w-full mx-auto">
            <div>
              <p className="font-plexmono text-[10px] uppercase tracking-[0.35em] text-[#D9A441] mb-6">Advisory Deal Book</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-[1.08] tracking-tight" data-testid="denied-headline">
                Track every crore in your advisory pipeline — as on today.
              </h1>
              <p className="mt-6 text-sm md:text-base text-neutral-400 leading-relaxed max-w-md">
                A private, single-tenant terminal for Mumbai real estate advisory. Weighted pipeline,
                stage funnels, and Cr / L formatted the way you actually read it.
              </p>
              <div className="mt-10 flex gap-4">
                {[
                  ["Deals", summary ? summary.deals : "—"],
                  ["Open Value", summary ? inr(summary.open_value) : "—"],
                  ["Weighted", summary ? inr(summary.weighted) : "—"],
                ].map(([label, value]) => (
                  <div key={label} className="border border-white/10 bg-black/40 px-5 py-4" data-testid={`denied-chip-${label.toLowerCase().replace(" ", "-")}`}>
                    <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">{label}</p>
                    <p className="text-xl text-white mt-1.5">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:justify-self-end w-full max-w-md">
              <div className="border border-white/10 bg-black/60 backdrop-blur p-8">
                <p className="font-plexmono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Sign In</p>
                <h2 className="text-2xl font-medium text-white">Access your pipeline</h2>
                <p className="text-sm text-neutral-400 mt-2">Continue with Google to open the advisory deal terminal. Data is scoped to your account.</p>
                <button onClick={() => googleLogin("/land-acquisition")} data-testid="dealbook-login-button" className="mt-6 w-full bg-[#D9A441] hover:bg-[#c8943a] text-black font-plexmono text-[11px] uppercase tracking-[0.2em] font-semibold px-6 py-4 flex items-center justify-center gap-2 transition-colors">
                  <LogIn size={15} /> Continue with Google
                </button>
                <div className="grid grid-cols-3 gap-3 mt-6">
                  {[["Weighted", TrendingUp], ["By Stage", BarChart3], ["Cr / L", IndianRupee]].map(([label, Icon]) => (
                    <div key={label} className="border border-white/10 py-3 flex flex-col items-center gap-1.5">
                      <Icon size={14} className="text-[#D9A441]" />
                      <span className="font-plexmono text-[9px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
                    </div>
                  ))}
                </div>
                <p className="font-plexmono text-[9px] text-neutral-600 mt-6">By continuing you agree to keep your deal book private to this account.</p>
              </div>
            </div>
          </div>
          <p className="font-plexmono text-[9px] text-neutral-600 uppercase tracking-[0.2em]">© 2026 Keyscape Advisory Services LLP · Clientele Pipeline</p>
        </div>
      </div>
    );
  }

  const invoice = invoiceDeal ? (() => {
    const fee = num(invoiceDeal.fee_potential);
    const gst = fee * 0.18;
    const tds = invoiceOpts.apply_tds ? fee * num(invoiceOpts.tds_rate) / 100 : 0;
    return { fee, gst, tds, total: fee + gst, net: fee + gst - tds };
  })() : null;

  return (
    <div className={`min-h-screen bg-[#0A0A0B] font-plex text-neutral-200 px-6 md:px-10 py-6 ${theme === "light" ? "light-theme" : ""}`} data-testid="dealbook-page">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-center justify-between pb-5 border-b border-white/10 gap-4 flex-wrap">
          <BrandLockup />
          <div className="flex items-center gap-2.5 flex-wrap justify-end">
            <Link to="/luxury-apartments" data-testid="dealbook-luxury-link" className="hidden xl:block font-plexmono text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[#D9A441] transition-colors mr-2">Luxury Apartments →</Link>
            <Link to="/employee-login" data-testid="dealbook-employee-link" className="hidden xl:block font-plexmono text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[#D9A441] transition-colors mr-2">Employee Login →</Link>
            <span className="hidden md:block font-plexmono text-[9px] text-neutral-500 uppercase tracking-[0.2em]" data-testid="db-as-on">
              as on {refreshedAt ? refreshedAt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
            </span>
            <button onClick={loadData} data-testid="db-refresh" title="Refresh" className="border border-white/15 hover:border-[#D9A441] text-neutral-300 p-2.5 transition-colors">
              <RefreshCw size={13} />
            </button>
            <button onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} data-testid="db-theme-toggle" className="border border-white/15 hover:border-[#D9A441] text-neutral-300 px-4 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors">
              {theme === "dark" ? <Sun size={13} /> : <Moon size={13} />} {theme === "dark" ? "Light" : "Dark"}
            </button>
            <label data-testid="db-upload" className={`border border-white/15 hover:border-[#D9A441] text-neutral-300 px-4 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors cursor-pointer ${uploading ? "opacity-50" : ""}`}>
              <Upload size={13} /> {uploading ? "Uploading…" : "Upload in Excel"}
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={uploadExcel} disabled={uploading} data-testid="db-upload-input" />
            </label>
            <button onClick={exportExcel} data-testid="dealbook-export" className="border border-white/15 hover:border-[#D9A441] text-neutral-300 px-4 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors">
              <Download size={13} /> Export in Excel
            </button>
            <button onClick={() => { setShowInvoices(true); loadInvoices(); }} data-testid="invoices-register-btn" className="border border-white/15 hover:border-[#D9A441] text-neutral-300 px-4 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors">
              <Receipt size={13} /> Invoices
            </button>
            <button onClick={() => setEditing({ ...EMPTY_DEAL })} data-testid="dealbook-new-deal" className="bg-[#D9A441] hover:bg-[#c8943a] text-black px-4 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2 transition-colors">
              <Plus size={13} /> Add New Deal
            </button>
            <button onClick={handleLogout} data-testid="dealbook-logout" className="text-neutral-500 hover:text-red-400 transition-colors ml-1" title="Sign out"><LogOut size={16} /></button>
          </div>
        </div>

        <div className="py-8 border-b border-white/10">
          <p className="font-plexmono text-[9px] uppercase tracking-[0.3em] text-neutral-500">Advisory Deal Book</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium text-white tracking-tight mt-1">Total Pipeline Value</h1>
          <div className="mt-3 flex items-end gap-6 flex-wrap">
            <p className="text-5xl sm:text-6xl font-light text-[#D9A441]" data-testid="db-total-pipeline">{inr(totalPipeline)}</p>
            <div className="pb-2 flex flex-wrap gap-x-6 gap-y-1 font-plexmono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <span data-testid="db-weighted">Weighted (prob-adj.) <span className="text-white">{inr(weighted)}</span></span>
              <span>{deals.length} deals on book</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-6">
          <KpiCard testid="kpi-open-deals" label="Open Deals" value={open.length} sub="excluding closed/rejected" />
          <KpiCard testid="kpi-avg" label="Avg Deal Size" value={inr(avgDeal)} sub="per open deal" />
          <KpiCard testid="kpi-fee" label="Advisory Fee Potential" value={inr(feePotential)} sub="@ avg fee on open pipeline" accent />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 pb-6">
          <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="db-stage-chart">
            <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">Pipeline by Stage</p>
            <p className="text-lg font-medium text-white tracking-tight mt-1">Value flowing through the funnel</p>
            <p className="font-plexmono text-[9px] text-neutral-600 mb-4 mt-1 uppercase tracking-[0.15em]">Values in ₹ Cr</p>
            <ChartBox className="h-80">
              {(cw, ch) => (
                <BarChart width={cw} height={ch} data={byStage} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <XAxis type="number" tickFormatter={(v) => (v >= 1e7 ? `${(v / 1e7).toFixed(0)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(0)}L` : v)} tick={{ fill: "#737373", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="stage" width={118} tick={{ fill: "#a3a3a3", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<StageTip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="value">
                    {byStage.map((s, i) => (<Cell key={i} fill={s.color} />))}
                  </Bar>
                </BarChart>
              )}
            </ChartBox>
          </div>

          <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="db-priority-chart">
            <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">By Priority</p>
            <p className="text-lg font-medium text-white tracking-tight mt-1 mb-4">Where the weight sits</p>
            <ChartBox className="h-56">
              {(cw, ch) => (
                <PieChart width={cw} height={ch}>
                  <Pie data={priorityData} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" strokeWidth={0}>
                    {priorityData.map((p) => (<Cell key={p.name} fill={PRIORITY_CHART_COLORS[p.name]} />))}
                  </Pie>
                  <Tooltip content={<PieTip total={priorityTotal} />} />
                </PieChart>
              )}
            </ChartBox>
            <div className="space-y-1.5 mt-2">
              {priorityData.map((p) => (
                <p key={p.name} className="flex items-center gap-2 font-plexmono text-[10px] text-neutral-400" data-testid={`priority-row-${p.name.toLowerCase()}`}>
                  <span className="w-2 h-2 shrink-0" style={{ background: PRIORITY_CHART_COLORS[p.name] }} />
                  {p.name}
                  <span className="ml-auto text-white">{priorityTotal ? Math.round((p.value / priorityTotal) * 100) : 0}%</span>
                  <span className="text-neutral-600">· {p.count}</span>
                </p>
              ))}
              <p className="flex items-center gap-2 font-plexmono text-[10px] text-neutral-400 pt-2 border-t border-white/10 mt-2" data-testid="priority-total">
                Total
                <span className="ml-auto text-white">{inr(priorityTotal)}</span>
              </p>
            </div>
          </div>

          <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="db-source-chart">
            <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">By Source</p>
            <p className="text-lg font-medium text-white tracking-tight mt-1">Origin mix</p>
            <p className="font-plexmono text-[9px] text-neutral-600 mb-4 mt-1 uppercase tracking-[0.15em]">Values shown in Indian numbering (Cr / L)</p>
            <ChartBox className="h-56">
              {(cw, ch) => (
                <PieChart width={cw} height={ch}>
                  <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" strokeWidth={0}>
                    {sourceData.map((s) => (<Cell key={s.name} fill={s.color} />))}
                  </Pie>
                  <Tooltip content={<PieTip total={sourceTotal} />} />
                </PieChart>
              )}
            </ChartBox>
            <div className="space-y-1.5 mt-2">
              {sourceData.map((s) => (
                <p key={s.name} className="flex items-center gap-2 font-plexmono text-[10px] text-neutral-400" data-testid={`source-row-${s.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <span className="w-2 h-2 shrink-0" style={{ background: s.color }} />
                  {s.name}
                  <span className="ml-auto text-white">{sourceTotal ? Math.round((s.value / sourceTotal) * 100) : 0}%</span>
                  <span className="text-neutral-600">· {inr(s.value)} · {s.count}</span>
                </p>
              ))}
              <p className="flex items-center gap-2 font-plexmono text-[10px] text-neutral-400 pt-2 border-t border-white/10 mt-2" data-testid="source-total">
                Total
                <span className="ml-auto text-white">{inr(sourceTotal)}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-white/[0.02] p-5 mb-6" data-testid="db-filters">
          <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mb-4">Pipeline Snapshot</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-3.5 text-neutral-500" />
              <input placeholder="Search client / address / source" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className={`${inputCls} pl-9`} data-testid="db-search" />
            </div>
            <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))} className={inputCls} data-testid="db-filter-source">
              <option value="">By Source</option>
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} className={inputCls} data-testid="db-filter-priority">
              <option value="">By Priority</option>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <p className="font-plexmono text-[10px] text-neutral-600 mt-4 uppercase tracking-[0.2em]">{filtered.length} deal{filtered.length === 1 ? "" : "s"} in view</p>
        </div>

        <div className="border border-white/10 bg-white/[0.02] mb-10 overflow-x-auto" data-testid="db-table-panel">
          <div className="px-5 pt-5 pb-1 flex items-center justify-between gap-4">
            <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">Deal Book</p>
            <select value={filters.stage} onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))} className={`${inputCls} w-44`} data-testid="db-stage-select">
              <option value="">All Stages</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <table className="w-full text-sm min-w-[1750px]" data-testid="db-table">
            <thead>
              <tr className="font-plexmono text-[9px] uppercase tracking-[0.2em] text-neutral-500 text-left border-b border-white/10">
                <th className="px-5 py-4 font-medium">Client</th>
                <th className="px-5 py-4 font-medium">Plot No.</th>
                <th className="px-5 py-4 font-medium">Landowner</th>
                <th className="px-5 py-4 font-medium">Contact</th>
                <th className="px-5 py-4 font-medium">Structure</th>
                <th className="px-5 py-4 font-medium">Address</th>
                <th className="px-5 py-4 font-medium text-right">Size (sq m)</th>
                <th className="px-5 py-4 font-medium text-right">Size (sq ft)</th>
                <th className="px-5 py-4 font-medium text-right">Rate</th>
                <th className="px-5 py-4 font-medium text-right">Market Value</th>
                <th className="px-5 py-4 font-medium text-right">Total Ask</th>
                <th className="px-5 py-4 font-medium">Stage</th>
                <th className="px-5 py-4 font-medium">Invoice</th>
                <th className="px-5 py-4 font-medium">Priority</th>
                <th className="px-5 py-4 font-medium">Source</th>
                <th className="px-5 py-4 font-medium text-right">Prob.</th>
                <th className="px-5 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={17} className="px-5 py-10 text-center text-neutral-500" data-testid="db-empty">No deals match your filters.</td></tr>
              )}
              {filtered.map((d) => (
                <tr key={d.deal_id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`db-row-${d.deal_id}`}>
                  <td className="px-5 py-4">
                    <p className="text-white">{d.client_name}</p>
                  </td>
                  <td className="px-5 py-4 max-w-[160px]" data-testid={`db-plot-${d.deal_id}`}>
                    <p className="font-plexmono text-[11px] text-neutral-300 whitespace-normal break-words" title={d.plot_no || ""}>
                      {d.plot_no ? (d.plot_no.length > 50 ? d.plot_no.slice(0, 50) + "…" : d.plot_no) : "—"}
                    </p>
                  </td>
                  <td className="px-5 py-4 text-neutral-300">{d.landowner_name || "—"}</td>
                  <td className="px-5 py-4">
                    <p className="text-neutral-300">{d.contact_person || "—"}</p>
                    {d.phone && <p className="font-plexmono text-[10px] text-neutral-500 mt-0.5">{d.phone}</p>}
                  </td>
                  <td className="px-5 py-4 text-neutral-300">{d.structure || "—"}</td>
                  <td className="px-5 py-4 text-neutral-300 max-w-[200px]"><p className="truncate" title={d.address || ""}>{[d.landmark, d.address].filter(Boolean).join(", ") || "—"}</p></td>
                  <td className="px-5 py-4 text-right font-plexmono text-neutral-300 whitespace-nowrap">{d.plot_sqm ? fmtNum(d.plot_sqm) : "—"}</td>
                  <td className="px-5 py-4 text-right font-plexmono text-neutral-300 whitespace-nowrap">{d.total_plot_sqft ? fmtNum(d.total_plot_sqft) : "—"}</td>
                  <td className="px-5 py-4 text-right font-plexmono text-neutral-300 whitespace-nowrap">{d.blended_rate ? `₹${fmtNum(d.blended_rate)}` : "—"}</td>
                  <td className="px-5 py-4 text-right font-plexmono text-neutral-300 whitespace-nowrap">{d.market_value ? inr(d.market_value) : "—"}</td>
                  <td className="px-5 py-4 text-right font-plexmono text-white whitespace-nowrap">{inr(d.total_ask)}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5 whitespace-nowrap" data-testid={`db-stage-cell-${d.deal_id}`}>
                      <button onClick={() => moveStage(d, -1)} disabled={STAGES.indexOf(d.stage) <= 0} data-testid={`db-stage-prev-${d.deal_id}`} title="Move to previous stage" className="text-neutral-500 hover:text-[#D9A441] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"><ChevronLeft size={14} /></button>
                      <span className="font-plexmono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 border border-white/15 text-neutral-300">{d.stage}</span>
                      <button onClick={() => moveStage(d, 1)} disabled={STAGES.indexOf(d.stage) < 0 || STAGES.indexOf(d.stage) >= STAGES.length - 1} data-testid={`db-stage-next-${d.deal_id}`} title="Move to next stage" className="text-neutral-500 hover:text-[#D9A441] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"><ChevronRight size={14} /></button>
                    </div>
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap" data-testid={`db-invoice-cell-${d.deal_id}`}>
                    {d.stage === "Closed Won" && (
                      <button
                        onClick={() => setInvoiceDeal(d)}
                        data-testid={`db-invoice-${d.deal_id}`}
                        className="bg-[#D9A441] hover:bg-[#c8943a] text-black font-plexmono text-[9px] uppercase tracking-[0.15em] font-semibold px-3 py-2 flex items-center gap-1.5 transition-colors"
                      >
                        <FileText size={12} /> Generate Invoice
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-4"><span className={`font-plexmono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 border whitespace-nowrap ${PRIORITY_COLORS[d.priority] || PRIORITY_COLORS.Medium}`}>{d.priority}</span></td>
                  <td className="px-5 py-4 text-neutral-300 whitespace-nowrap">{d.source || "—"}</td>
                  <td className="px-5 py-4 text-right font-plexmono text-neutral-300 whitespace-nowrap">{num(d.probability) ? `${d.probability}%` : "—"}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-3">
                      {d.phone && (
                        <a href={waDealLink(d)} target="_blank" rel="noopener noreferrer" data-testid={`db-whatsapp-${d.deal_id}`} title={`WhatsApp ${d.contact_person || d.client_name}`} className="text-emerald-500 hover:text-emerald-400 transition-colors"><MessageCircle size={15} /></a>
                      )}
                      <button onClick={() => setEditing({ ...EMPTY_DEAL, ...d })} data-testid={`db-edit-${d.deal_id}`} title="Edit Deal" className="text-neutral-500 hover:text-white transition-colors"><Pencil size={15} /></button>
                      <button onClick={() => removeDeal(d)} data-testid={`db-delete-${d.deal_id}`} title="Delete Deal" className="text-neutral-500 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editing && (
          <DealDrawer editing={editing} saving={saving} set={set} auto={auto} onSubmit={saveDeal} onClose={() => setEditing(null)} />
        )}

        {invoiceDeal && invoice && (
          <InvoiceDrawer
            deal={invoiceDeal}
            invoice={invoice}
            opts={invoiceOpts}
            setOpts={setInvoiceOpts}
            paymentTerms={paymentTerms}
            setPaymentTerms={setPaymentTerms}
            firmSettings={firmSettings}
            emailSending={emailSending}
            onClose={() => setInvoiceDeal(null)}
            onDownload={downloadPdf}
            onEmail={() => emailInvoice()}
            onSaveDefaults={saveFirmSettings}
          />
        )}

        {dupDialog && (
          <div className="fixed inset-0 z-50" data-testid="dup-dialog-overlay">
            <div className="absolute inset-0 bg-black/70" onClick={() => setDupDialog(null)} />
            <div className="absolute inset-x-4 top-[8%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[560px] bg-[#111112] border border-white/10 p-8 font-plex max-h-[84vh] overflow-y-auto" data-testid="dup-dialog">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="font-plexmono text-[10px] uppercase tracking-[0.3em] text-[#D9A441]">Duplicate Records Found</p>
                  <h2 className="text-2xl font-medium text-white mt-2">{dupDialog.duplicates.length} deal{dupDialog.duplicates.length === 1 ? "" : "s"} already on the book</h2>
                </div>
                <button onClick={() => setDupDialog(null)} data-testid="dup-dialog-close" className="text-neutral-500 hover:text-white transition-colors"><X size={18} /></button>
              </div>
              <ul className="space-y-2 mb-8">
                {dupDialog.duplicates.map((d, i) => (
                  <li key={i} className="border border-white/10 px-4 py-3 flex items-center justify-between" data-testid={`dup-row-${i}`}>
                    <div>
                      <p className="text-sm text-white">{d.client_name}</p>
                      <p className="font-plexmono text-[10px] text-neutral-500 mt-0.5">{[d.plot_no && `Plot ${d.plot_no}`, d.landmark].filter(Boolean).join(" · ") || "—"}</p>
                    </div>
                    <span className="font-plexmono text-[11px] text-[#D9A441]">{inr(d.total_ask)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-neutral-400 mb-6">{dupDialog.new_records} new record(s) will be imported regardless. For the duplicates above, choose:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button onClick={() => resolveDupes("overwrite")} data-testid="dup-overwrite" className="bg-[#D9A441] hover:bg-[#c8943a] text-black px-4 py-3.5 font-plexmono text-[10px] uppercase tracking-[0.15em] font-semibold transition-colors">Overwrite Existing</button>
                <button onClick={() => resolveDupes("create")} data-testid="dup-create" className="border border-white/15 hover:border-[#D9A441] text-neutral-200 px-4 py-3.5 font-plexmono text-[10px] uppercase tracking-[0.15em] transition-colors">Create New Record</button>
                <button onClick={() => resolveDupes("skip")} data-testid="dup-skip" className="border border-white/15 hover:border-white/40 text-neutral-400 px-4 py-3.5 font-plexmono text-[10px] uppercase tracking-[0.15em] transition-colors">Skip Duplicates</button>
              </div>
            </div>
          </div>
        )}
        {showInvoices && (
          <InvoiceRegister
            invoices={invoices}
            reminderSending={reminderSending}
            digestSending={digestSending}
            emailSending={emailSending}
            onClose={() => setShowInvoices(false)}
            onExport={exportInvoices}
            onDigest={sendDigest}
            onPaymentStatus={setPaymentStatus}
            onAmountReceived={setAmountReceived}
            onDownload={downloadInvoicePdf}
            onRemind={sendReminder}
            onEmail={(inv) => emailInvoice({ deal_id: inv.id })}
          />
        )}
      </div>
    </div>
  );
}
